/**
 * Flightline — Dynamic fuel spot market and hedging
 * --------------------------------------------------------------------------
 * Location : /src/domain/events/fuelMarket.ts
 *
 * Spot price model
 *   base(day)  a stateless random walk bounded to $2.00 - $4.50 per US gallon.
 *              It is built from five octaves of seeded value noise (periods of
 *              150, 60, 21, 7 and 3 days) passed through a soft squash. It
 *              behaves like a mean-reverting walk: slow drift, medium swings
 *              and day-to-day jitter. Because it
 *              is a pure function of (seed, day) it needs no stored state, is
 *              replay-safe, and can be evaluated for any past or future day.
 *   spot(day)  base(day) x product of active FUEL_PRICE_SHOCK multipliers.
 *              The shock is applied after the $2.00 - $4.50 band, so a severe
 *              shock can push the paid price above $4.50.
 *
 * Hedging
 *   `FuelHedgeContract` (from the models) locks a price per gallon for a
 *   monthly barrel volume. `calculateNetFuelCost` fills each day's burn from
 *   the cheapest live contract first, up to its daily share of the monthly
 *   volume, and buys the rest on the spot market. Unused monthly volume is not
 *   banked: a hedge that is bigger than the burn is a real cost.
 */

import { clamp, hashString } from '../ai/aiMath';
import type { FuelHedgeContract, HedgeContractId, HedgeTermMonths, SimDay, SimMinutes } from '../models';
import { DEFAULT_SIM_EPOCH_UTC_MS } from '../demand/simTime';
import { effectiveMultiplier, resolveEventPhase, toSimDay } from './lifecycle';
import type {
  FuelMarketOptions,
  FuelPriceState,
  FuelTrend,
  HedgeUsage,
  MacroEvent,
  MacroEventId,
} from './types';

/* ========================================================================== *
 * 1. CONSTANTS
 * ========================================================================== */

export const FUEL_PRICE_FLOOR_PER_GALLON_USD = 2.0;
export const FUEL_PRICE_CEILING_PER_GALLON_USD = 4.5;
/** Centre of the band; the long-run mean the walk reverts to. */
export const FUEL_LONG_RUN_MEAN_PER_GALLON_USD =
  (FUEL_PRICE_FLOOR_PER_GALLON_USD + FUEL_PRICE_CEILING_PER_GALLON_USD) / 2;
/** Hard ceiling on the price actually paid, shocks included. */
export const FUEL_SHOCK_PRICE_CAP_PER_GALLON_USD = 12;
/** Cap on the combined shock multiplier. */
export const MAX_SHOCK_MULTIPLIER = 3;

export const GALLONS_PER_BARREL = 42;
/** Jet A-1 at 0.8 kg/L: 3.7854 L per US gallon. */
export const KG_PER_US_GALLON = 3.0283;
export const DAYS_PER_MONTH = 30.4375;
const MINUTES_PER_DAY = 1440;

/** A daily move smaller than this (as a fraction) reads as STABLE. */
const TREND_BAND = 0.0075;

const OCTAVE_PERIODS_DAYS: readonly number[] = [150, 60, 21, 7, 3];
const OCTAVE_AMPLITUDES: readonly number[] = [0.5, 0.3, 0.18, 0.1, 0.05];
const OCTAVE_AMPLITUDE_SUM = OCTAVE_AMPLITUDES.reduce((total, amplitude) => total + amplitude, 0);
const HALF_BAND = (FUEL_PRICE_CEILING_PER_GALLON_USD - FUEL_PRICE_FLOOR_PER_GALLON_USD) / 2;
/**
 * Soft-squash gain. The summed octaves are bell-shaped and rarely reach their
 * extremes; `tanh` stretches the middle so the walk uses most of the band, yet
 * still approaches the edges smoothly instead of sitting on a flat clamp.
 */
const SQUASH_GAIN = 1.8;
const SQUASH_NORMALISER = Math.tanh(SQUASH_GAIN);

/** Desk fee on a forward contract: base plus a per-month term premium. */
const HEDGE_DESK_BASE_FEE = 0.015;
const HEDGE_DESK_FEE_PER_MONTH = 0.004;
/** Months over which a shock or dip in spot washes out of the forward curve. */
const FORWARD_REVERSION_MONTHS = 5;

/* ========================================================================== *
 * 2. THE RANDOM WALK
 * ========================================================================== */

function latticeValue(seed: number, octave: number, index: number): number {
  const hash = hashString(`fuel|${seed}|${octave}|${index}`);
  return (hash / 4294967295) * 2 - 1;
}

function smoothstep(t: number): number {
  return t * t * (3 - 2 * t);
}

function octaveNoise(seed: number, octave: number, periodDays: number, day: number): number {
  const position = day / periodDays;
  const lower = Math.floor(position);
  const t = smoothstep(position - lower);
  const a = latticeValue(seed, octave, lower);
  const b = latticeValue(seed, octave, lower + 1);
  return a + (b - a) * t;
}

function roundTo(value: number, decimals: number): number {
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
}

/** Base (shock-free) spot price in USD per US gallon on a sim day. Always within $2.00 - $4.50. */
export function baseFuelPricePerGallon(simDay: SimDay, worldSeed: number = 0): number {
  let accumulated = 0;
  for (let octave = 0; octave < OCTAVE_PERIODS_DAYS.length; octave += 1) {
    const period = OCTAVE_PERIODS_DAYS[octave] ?? 1;
    const amplitude = OCTAVE_AMPLITUDES[octave] ?? 0;
    accumulated += amplitude * octaveNoise(worldSeed, octave, period, simDay);
  }
  const normalised = accumulated / OCTAVE_AMPLITUDE_SUM;
  const squashed = Math.tanh(SQUASH_GAIN * normalised) / SQUASH_NORMALISER;
  const price = FUEL_LONG_RUN_MEAN_PER_GALLON_USD + squashed * HALF_BAND;
  return roundTo(
    clamp(price, FUEL_PRICE_FLOOR_PER_GALLON_USD, FUEL_PRICE_CEILING_PER_GALLON_USD),
    3,
  );
}

/* ========================================================================== *
 * 3. SHOCKS AND THE SPOT PRICE
 * ========================================================================== */

interface ShockReading {
  readonly multiplier: number;
  readonly eventIds: readonly MacroEventId[];
}

function readShocks(events: readonly MacroEvent[], simDay: SimDay): ShockReading {
  let multiplier = 1;
  const eventIds: MacroEventId[] = [];
  for (const event of events) {
    if (event.category !== 'FUEL_PRICE_SHOCK') continue;
    const phase = resolveEventPhase(event, simDay);
    if (phase.intensity <= 0) continue;
    multiplier *= effectiveMultiplier(event.impact.fuelPriceMultiplier, phase.intensity);
    eventIds.push(event.id);
  }
  return { multiplier: Math.min(MAX_SHOCK_MULTIPLIER, multiplier), eventIds };
}

function spotFor(base: number, shockMultiplier: number): number {
  return roundTo(Math.min(FUEL_SHOCK_PRICE_CAP_PER_GALLON_USD, base * shockMultiplier), 3);
}

function trendOf(changeFraction: number): FuelTrend {
  if (changeFraction > TREND_BAND) return 'RISING';
  if (changeFraction < -TREND_BAND) return 'FALLING';
  return 'STABLE';
}

/**
 * Jet-fuel spot market for the day of `simDate`.
 *
 * Only events whose schedule puts them in effect on that day count; the
 * schedule (not the stored `state`) is used so a stale event list cannot
 * produce a stale price.
 */
export function calculateCurrentFuelPrice(
  simDate: Date,
  activeEvents: readonly MacroEvent[],
  options: FuelMarketOptions = {},
): FuelPriceState {
  const epoch = options.epochUtcMs ?? DEFAULT_SIM_EPOCH_UTC_MS;
  const seed = options.worldSeed ?? 0;
  const simDay = toSimDay(simDate, epoch);

  const base = baseFuelPricePerGallon(simDay, seed);
  const shock = readShocks(activeEvents, simDay);
  const spot = spotFor(base, shock.multiplier);

  const previousBase = baseFuelPricePerGallon(simDay - 1, seed);
  const previousShock = readShocks(activeEvents, simDay - 1);
  const previousSpot = spotFor(previousBase, previousShock.multiplier);

  const changeFraction = previousSpot > 0 ? (spot - previousSpot) / previousSpot : 0;

  return {
    simDay,
    baseSpotPerGallonUSD: base,
    shockMultiplier: roundTo(shock.multiplier, 4),
    spotPerGallonUSD: spot,
    spotPerKgUSD: roundTo(spot / KG_PER_US_GALLON, 4),
    previousDaySpotPerGallonUSD: previousSpot,
    dailyChangePct: roundTo(changeFraction * 100, 3),
    trend: trendOf(changeFraction),
    activeShockEventIds: shock.eventIds,
  };
}

/* ========================================================================== *
 * 4. HEDGING
 * ========================================================================== */

export interface NetFuelCostInput {
  /** US gallons burned in the period. */
  readonly gallons: number;
  readonly fuelPrice: FuelPriceState;
  /** The player's contracts (`CompanyHQ.fuelHedges`). */
  readonly hedges: readonly FuelHedgeContract[];
  readonly nowMinutes: SimMinutes;
  /** Days the burn covers. One daily call is the intended use. Default 1. */
  readonly periodDays?: number;
}

export interface NetFuelCostResult {
  readonly gallons: number;
  readonly hedgedGallons: number;
  readonly spotGallons: number;
  readonly hedgedCostUSD: number;
  readonly spotCostUSD: number;
  readonly totalCostUSD: number;
  /** Blended price paid per gallon; the spot price when no gallons were burned. */
  readonly effectivePricePerGallonUSD: number;
  /** What the same gallons would have cost entirely on the spot market. */
  readonly unhedgedCostUSD: number;
  /** Positive when hedges beat the spot price, negative when they cost more. */
  readonly hedgeSavingsUSD: number;
  readonly usage: readonly HedgeUsage[];
  /** Input contracts with `barrelsRemaining` reduced by what was drawn. Same order as the input. */
  readonly updatedHedges: readonly FuelHedgeContract[];
}

/** A contract is live from `startedAt` (inclusive) to `expiresAt` (exclusive) with barrels left. */
export function isHedgeLive(hedge: FuelHedgeContract, nowMinutes: SimMinutes): boolean {
  return nowMinutes >= hedge.startedAt && nowMinutes < hedge.expiresAt && hedge.barrelsRemaining > 0;
}

export function liveHedges(
  hedges: readonly FuelHedgeContract[],
  nowMinutes: SimMinutes,
): FuelHedgeContract[] {
  return hedges.filter((hedge) => isHedgeLive(hedge, nowMinutes));
}

/** Drops contracts that have expired or are used up. */
export function pruneHedges(
  hedges: readonly FuelHedgeContract[],
  nowMinutes: SimMinutes,
): FuelHedgeContract[] {
  return hedges.filter((hedge) => nowMinutes < hedge.expiresAt && hedge.barrelsRemaining > 0);
}

/**
 * Net cost of buying `gallons` with the player's hedges in force.
 *
 * Each live contract can supply at most `barrelsPerMonth x periodDays / 30.4375`
 * barrels per call, and never more than it has left. Cheapest contracts fill first.
 */
export function calculateNetFuelCost(input: NetFuelCostInput): NetFuelCostResult {
  const gallons = Number.isFinite(input.gallons) ? Math.max(0, input.gallons) : 0;
  const periodDays = Math.max(0, input.periodDays ?? 1);
  const spotPrice = input.fuelPrice.spotPerGallonUSD;

  const candidates = input.hedges
    .map((hedge, index) => ({ hedge, index }))
    .filter(({ hedge }) => isHedgeLive(hedge, input.nowMinutes))
    .sort((a, b) => a.hedge.pricePerGallonUSD - b.hedge.pricePerGallonUSD || a.index - b.index);

  let unfilled = gallons;
  let hedgedGallons = 0;
  let hedgedCostUSD = 0;
  const usage: HedgeUsage[] = [];
  const barrelsDrawn = new Map<number, number>();

  for (const { hedge, index } of candidates) {
    if (unfilled <= 0) break;
    const periodCapBarrels = (hedge.barrelsPerMonth * periodDays) / DAYS_PER_MONTH;
    const availableBarrels = Math.min(hedge.barrelsRemaining, periodCapBarrels);
    const covered = Math.min(unfilled, availableBarrels * GALLONS_PER_BARREL);
    if (covered <= 0) continue;
    const barrels = covered / GALLONS_PER_BARREL;
    unfilled -= covered;
    hedgedGallons += covered;
    hedgedCostUSD += covered * hedge.pricePerGallonUSD;
    barrelsDrawn.set(index, barrels);
    usage.push({
      contractId: hedge.id,
      priceLockedPerGallonUSD: hedge.pricePerGallonUSD,
      gallonsCovered: covered,
      barrelsConsumed: barrels,
    });
  }

  const spotGallons = Math.max(0, gallons - hedgedGallons);
  const spotCostUSD = spotGallons * spotPrice;
  const totalCostUSD = hedgedCostUSD + spotCostUSD;
  const unhedgedCostUSD = gallons * spotPrice;

  const updatedHedges = input.hedges.map((hedge, index) => {
    const drawn = barrelsDrawn.get(index);
    if (drawn === undefined) return hedge;
    return { ...hedge, barrelsRemaining: Math.max(0, hedge.barrelsRemaining - drawn) };
  });

  return {
    gallons,
    hedgedGallons,
    spotGallons,
    hedgedCostUSD,
    spotCostUSD,
    totalCostUSD,
    effectivePricePerGallonUSD: gallons > 0 ? totalCostUSD / gallons : spotPrice,
    unhedgedCostUSD,
    hedgeSavingsUSD: unhedgedCostUSD - totalCostUSD,
    usage,
    updatedHedges,
  };
}

/**
 * Forward price per gallon the Fuel Hedging Desk would lock today for a term.
 *
 * The expected spot price reverts from today's level (shock included) toward
 * the long-run mean over about five months, and the desk adds a fee that grows
 * with the term. A hedge struck in the middle of a shock therefore locks in a
 * premium, which is the point of hedging before an announced one.
 */
export function quoteFuelHedgePricePerGallon(
  fuelPrice: FuelPriceState,
  termMonths: HedgeTermMonths,
): number {
  const reversion = Math.exp(-termMonths / FORWARD_REVERSION_MONTHS);
  const expected =
    FUEL_LONG_RUN_MEAN_PER_GALLON_USD +
    (fuelPrice.spotPerGallonUSD - FUEL_LONG_RUN_MEAN_PER_GALLON_USD) * reversion;
  const fee = 1 + HEDGE_DESK_BASE_FEE + HEDGE_DESK_FEE_PER_MONTH * termMonths;
  return roundTo(clamp(expected * fee, 1.5, FUEL_SHOCK_PRICE_CAP_PER_GALLON_USD), 3);
}

export interface NewFuelHedgeInput {
  readonly id: HedgeContractId;
  readonly termMonths: HedgeTermMonths;
  readonly barrelsPerMonth: number;
  readonly fuelPrice: FuelPriceState;
  readonly nowMinutes: SimMinutes;
}

/** Builds a `FuelHedgeContract` at the desk's current quote. */
export function createFuelHedgeContract(input: NewFuelHedgeInput): FuelHedgeContract {
  const barrelsPerMonth = Math.max(0, Math.floor(input.barrelsPerMonth));
  const termMinutes = Math.round(input.termMonths * DAYS_PER_MONTH * MINUTES_PER_DAY);
  return {
    id: input.id,
    pricePerGallonUSD: quoteFuelHedgePricePerGallon(input.fuelPrice, input.termMonths),
    barrelsPerMonth,
    barrelsRemaining: barrelsPerMonth * input.termMonths,
    termMonths: input.termMonths,
    startedAt: input.nowMinutes,
    expiresAt: input.nowMinutes + termMinutes,
  };
}
